import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { ArrowRight, ShieldCheck, CalendarClock, DollarSign, ChefHat, Building2, PercentSquare } from "lucide-react";
import { PopularSection } from "@/components/PopularSection";

export default function Landing() {
  return (
    <div className="min-h-screen grain">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden border-b hairline">
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-24 grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <div className="tick-label text-[#FF3B30] mb-6" data-testid="hero-eyebrow">Kitchens by the hour · Licensed · Insured</div>
            <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-8xl leading-none tracking-tighter">
              Rent a<br />
              <span className="text-[#FF3B30]">commercial</span><br />
              kitchen tonight.
            </h1>
            <p className="mt-8 max-w-lg text-lg text-neutral-300 leading-relaxed">
              A marketplace connecting food entrepreneurs with restaurants, food halls, and churches
              renting out fully-licensed kitchen space by the hour.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link data-testid="hero-browse-btn" to="/browse" className="btn-signal inline-flex items-center gap-2">
                Find a kitchen <ArrowRight size={16} />
              </Link>
              <Link data-testid="hero-list-btn" to="/list-your-kitchen" className="btn-outline">
                List your space
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1782750161991-23529c9462bb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwzfHxjb21tZXJjaWFsJTIwa2l0Y2hlbiUyMHN0YWlubGVzcyUyMHN0ZWVsfGVufDB8fHx8MTc4NzM4OTQyMXww&ixlib=rb-4.1.0&q=85"
                alt="Commercial kitchen"
                className="w-full h-[520px] object-cover border hairline"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#0a0a0a] border hairline p-5 w-56">
                <div className="tick-label">Avg. Kitchen</div>
                <div className="font-mono text-3xl font-bold">$42/hr</div>
                <div className="text-neutral-400 text-sm mt-1">Verified in 12 cities</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="border-b hairline">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="tick-label">01 · Marketplace</div>
          <h2 className="font-display font-black text-4xl lg:text-5xl mt-3 tracking-tight max-w-3xl">
            Two sides. Zero friction.<br />Fifteen percent flat.
          </h2>
          <div className="grid md:grid-cols-3 gap-0 mt-14 border hairline">
            {[
              { icon: <ChefHat size={24} />, t: "Cooks", d: "Upload your food handler license once. Book any verified kitchen." },
              { icon: <Building2 size={24} />, t: "Hosts", d: "Set your rate. Approve each cook. Fill idle hours with revenue." },
              { icon: <PercentSquare size={24} />, t: "15% platform", d: "Simple flat fee added at checkout. Stripe handles the money." },
            ].map((it, i) => (
              <div key={i} className={`p-10 ${i < 2 ? "md:border-r hairline" : ""}`}>
                <div className="text-[#FF3B30] mb-6">{it.icon}</div>
                <h3 className="font-display text-2xl font-bold mb-3">{it.t}</h3>
                <p className="text-neutral-400 leading-relaxed">{it.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="border-b hairline">
        <div className="max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-2 gap-16 items-center">
          <img
            src="https://images.unsplash.com/photo-1769955817432-641929f613f0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHxjaGVmJTIwY29va2luZyUyMHByb2Zlc3Npb25hbCUyMGtpdGNoZW58ZW58MHx8fHwxNzg3Mzg5NDIxfDA&ixlib=rb-4.1.0&q=85"            alt="Chef at work"
            className="w-full h-[600px] object-cover border hairline"
          />
          <div>
            <div className="tick-label">02 · How it works</div>
            <h2 className="font-display font-black text-4xl lg:text-5xl mt-3 tracking-tight">
              Book in four steps.
            </h2>
            <ol className="mt-10 space-y-6">
              {[
                { i: <ShieldCheck size={22} />, t: "Verify license", d: "Cooks upload their food handler certificate for host review." },
                { i: <CalendarClock size={22} />, t: "Pick a window", d: "Choose start and end time. Instant book or approval-required." },
                { i: <DollarSign size={22} />, t: "Pay securely", d: "Stripe checkout · card or Apple Pay. 15% platform fee shown up front." },
                { i: <ArrowRight size={22} />, t: "Cook. Rate. Repeat.", d: "Message your host, leave a review after the shift ends." },
              ].map((s, i) => (
                <li key={i} className="flex gap-5 border-b hairline pb-6">
                  <div className="text-[#FF3B30] pt-1">{s.i}</div>
                  <div className="flex-1">
                    <div className="font-display text-xl font-bold">{s.t}</div>
                    <div className="text-neutral-400 mt-1">{s.d}</div>
                  </div>
                  <div className="font-mono text-neutral-600 text-xs pt-1">0{i + 1}</div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* CTA */}
      <PopularSection />

      <section className="max-w-7xl mx-auto px-6 py-24 text-center">
        <h2 className="font-display font-black text-5xl lg:text-7xl tracking-tighter">
          Ready to <span className="text-[#FF3B30]">fire the burners?</span>
        </h2>
        <div className="mt-10 flex justify-center gap-4">
          <Link data-testid="cta-register-cook" to="/register?role=cook" className="btn-signal">Join as a cook</Link>
          <Link data-testid="cta-register-owner" to="/register?role=owner" className="btn-outline">Host your kitchen</Link>
        </div>
      </section>

      <footer className="border-t hairline">
        <div className="max-w-7xl mx-auto px-6 py-8 flex justify-between tick-label">
          <span>© KitchenShare 2026</span>
          <span>Powered by Stripe · Emergent</span>
        </div>
      </footer>
    </div>
  );
}
